import type { FirstTierSupplier } from "@/api/supplierService";
import { TableCell, Autocomplete, TextField } from "@mui/material";
import { useMemo } from "react";

interface Supplier {
  id: number;
  name: string;
}

interface SupplierSelectFieldProps {
  value: number | string;
  onChange: (value: number) => void;
  suppliers: Supplier[] | FirstTierSupplier[] | undefined;
  width?: number;
  error?: boolean;
}

const SupplierSelectField = ({
  value,
  onChange,
  suppliers,
  width = 150,
  error = false,
}: SupplierSelectFieldProps) => {
  const supplierOptions = useMemo(() => {
    if (!suppliers) return [];
    return suppliers.map((supplier) => ({
      label: (supplier as Supplier).name
        ? (supplier as Supplier).name
        : `${(supplier as FirstTierSupplier).supplier_abbr ? `${(supplier as FirstTierSupplier).supplier_abbr} - ` : ""}${(supplier as FirstTierSupplier).supplier_code}`,
      value: supplier.id,
    }));
  }, [suppliers]);

  return (
    <TableCell>
      <Autocomplete
        size="small"
        value={supplierOptions.find((option) => option.value === value) || null}
        onChange={(_e, newValue) => {
          onChange(Number(newValue?.value));
        }}
        options={supplierOptions}
        slotProps={{
          popper: {
            style: {
              width: "fit-content",
            },
            modifiers: [
              {
                name: "flip",
                enabled: true,
              },
            ],
          },
          listbox: {
            style: {
              whiteSpace: "nowrap",
              width: "fit-content",
              minWidth: "100%",
            },
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            error={error}
            sx={{
              minWidth: String(width) + "px",
              "& .MuiInputBase-input": {
                whiteSpace: "nowrap",
                overflow: "visible",
                textOverflow: "clip",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: error ? "#d32f2f" : undefined,
                borderWidth: error ? 2 : 1,
              },
            }}
            slotProps={{
              input: {
                ...params.InputProps,
                style: { fontSize: "0.875rem" },
              },
            }}
          />
        )}
      />
    </TableCell>
  );
};

export default SupplierSelectField;
